import { setupLayouts } from 'virtual:meta-layouts'
import type { App } from 'vue'
import { watch } from 'vue'

import type { RouteRecordRaw } from 'vue-router/auto'

import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { createRouter, createWebHashHistory, createWebHistory } from 'vue-router/auto'
import { START_LOCATION } from 'vue-router'
import { endRouteLoading, startRouteLoading } from '@/composables/useRouteLoading'
import { hideBootSplash } from '@/utils/bootSplash'
import { loadAuth } from '@/helper/localStorage'
import { isAppleTouchDevice, nativeAppMode } from '@/utils/nativeAppMode'
import { clearInAppHistory, popInAppHistory, pushInAppHistory } from '@/utils/inAppHistory'
import { getShellScroller } from '@/utils/appScroller'
import { getDefaultAuthenticatedRoute } from '@/navigation/vertical'
import { authStorage, consumeSessionExpired, setNavigationInFlight } from '@/services/authService'
import {
  buildAccessSnapshotFromRoles,
  buildDeniedAccessSnapshotFromRoles,
  getPageKeyForRoute,
  isPermissionRoleFallbackEnabled,
} from '@/config/permissionManifest'
import {
  canPage,
  getAccessSnapshot,
  isClinicReceptionOnlyUser,
  saveAccessSnapshot,
  savePermissions,
} from '@/helper/permissionHelper'
import { GetCurrentAccess } from '@/api/permissionsApi'
import { isOffline } from '@/services/offlineSignal'
import { axiosInstance } from '@/plugins/axios'
// Resolved at call time instead of importing `store` from '@/plugins/2.pinia',
// which put this module inside the startup cycle
// httpClient -> 1.router -> 2.pinia -> cashier.store -> posProfileApi -> httpClient.
// See the longer note in src/services/httpClient.ts.
import { getActivePinia } from 'pinia'
import { useAuthorizationWarningStore } from '@/stores/authorizationWarning.store'
import { useWorkspaceTabsStore } from '@/stores/workspaceTabs.store'
// ⚠️ NOT `@/stores/posSales.store` — that pulls in cashier.store → posProfileApi →
// httpClient → 1.router, the exact startup cycle the note at the top of this file
// warns about. `@/type/pos` is `import type` only plus this pure predicate, so it
// adds no runtime edge; the store's state is read from the pinia instance instead.
import { posSaleSlotHasWork } from '@/type/pos'
import { isWorkspaceTabRoute, workspaceTabDescriptor } from '@/config/workspaceTabs'
import { getI18n } from '@/plugins/i18n'

/**
 * True when this navigation is a move between two already-open workspace tabs.
 * Used to suppress the default scroll-to-top so each tab keeps its own offset.
 */
function isWorkspaceTabSwitch(to: { name?: unknown }, from: { name?: unknown }) {
  if (!isWorkspaceTabRoute(to.name))
    return false

  const tabs = useWorkspaceTabsStore(getActivePinia())

  return Boolean(tabs.find(String((to as { fullPath?: string }).fullPath ?? ''))) && isWorkspaceTabRoute(from.name)
}

/**
 * Tab label: the record type plus its id, so two open visits are distinguishable.
 * Falls back to the raw id if translations are not ready yet.
 */
function resolveWorkspaceTabTitle(to: { name?: unknown, params?: Record<string, unknown> }) {
  const descriptor = workspaceTabDescriptor(to.name)
  if (!descriptor)
    return ''

  const raw = to.params?.[descriptor.idParam ?? 'id']
  const id = String(Array.isArray(raw) ? raw[0] : (raw ?? '')).trim()

  let label = ''
  try {
    label = String(getI18n().global.t(descriptor.labelKey))
  }
  catch {
    // Translations not ready — fall back to the bare id below.
  }

  if (label && id)
    return `${label} · ${id}`

  return label || id
}

function recursiveLayouts(route: RouteRecordRaw): RouteRecordRaw {
  if (route.children) {
    for (let i = 0; i < route.children.length; i++)
      route.children[i] = recursiveLayouts(route.children[i])

    return route
  }

  return setupLayouts([route])[0]
}

function isElectronRuntime() {
  if (typeof window === 'undefined')
    return false

  return Boolean(window.alkokhDesktop?.isElectron || navigator.userAgent.includes('Electron'))
}

const routerHistory = isElectronRuntime()
  ? createWebHashHistory(import.meta.env.BASE_URL)
  : createWebHistory(import.meta.env.BASE_URL)

/**
 * ⚠️ NATIVE-APP MODE: THE BROWSER HISTORY NEVER GROWS (phone shell + installed PWA, see
 * `useNativeAppMode`).
 *
 * iOS gives an installed web app an edge-swipe that walks browser history, and nothing in CSS or JS
 * can switch it off. The old approach blocked the pop after the fact, but the swipe still animated,
 * flashed the previous page and, through the overlay-close it triggered, threw away an open
 * create/edit dialog. So while native-app mode is on, every router PUSH is written as a REPLACE: the
 * browser never gets an entry behind or ahead of the current one, and the swipe has nowhere to go.
 * The locations being left go onto the in-app stack (`src/utils/inAppHistory.ts`) instead.
 *
 * In-app back buttons MUST call `navigateBack()`, never `router.back()`.
 *
 * Any pop that still arrives (the Android system back, see `installAndroidBackSentinel`) is flagged
 * here — this listener is registered BEFORE `createRouter`, so it runs ahead of the router's own one —
 * and `blockHistoryNavigation` (the FIRST `beforeEach`) aborts it, after which vue-router steps
 * history back to where it was. Those restoring pops are paused and never reach listeners.
 */
let pendingHistoryPop = false

/** Set by `beginRootNavigation()`: the next push starts a fresh stack instead of recording. */
let nextPushIsRoot = false

routerHistory.listen(() => {
  pendingHistoryPop = true
})

const browserPush = routerHistory.push.bind(routerHistory)

routerHistory.push = (to, data) => {
  if (!nativeAppMode.value) {
    browserPush(to, data)

    return
  }

  if (nextPushIsRoot)
    clearInAppHistory()
  else
    pushInAppHistory(routerHistory.location)

  nextPushIsRoot = false

  // `data` carries vue-router's own state plus any `router.push({ state })` payload (preventive
  // care reads one back from `history.state`); `replace` merges it exactly as `push` would.
  routerHistory.replace(to, data)
}

/**
 * A bottom-tab tap: tab roots have nothing to go back to, so the stack restarts there — the way a
 * native tab bar behaves. Call it right before the navigation; a navigation that never lands leaves
 * the flag to be cleared in `afterEach`.
 */
export function beginRootNavigation() {
  nextPushIsRoot = true
}

/**
 * The only sanctioned in-app "back".
 *
 * Native-app mode: pops the in-app stack. With nothing to pop (a cold launch onto a deep link), it
 * goes to `fallback`, or the user's home page. Elsewhere (desktop, a browser tab) it is the browser's
 * back when there is an in-app entry behind us, and the same fallback when there is not — a bare
 * `router.back()` would otherwise do nothing, or leave the app.
 */
export function navigateBack(fallback?: RouteLocationRaw) {
  const home = () => fallback ?? getDefaultAuthenticatedRoute()

  if (nativeAppMode.value) {
    const previous = popInAppHistory(router.currentRoute.value.fullPath)

    return router.replace(previous ?? home())
  }

  // Resolves once the pop has settled (`afterEach` fires for failed navigations too), so a caller
  // can tell whether it landed.
  if (routerHistory.state?.back) {
    return new Promise<void>(resolve => {
      const stop = router.afterEach(() => {
        stop()
        resolve()
      })

      router.back()
    })
  }

  return router.push(home())
}

/**
 * ⚠️ ANDROID ONLY. With replace-only history the app's entry is the first one in the tab, so the
 * Android system back (button or gesture) would close the installed app from any screen. One spare
 * entry for the same URL gives that back something to pop; `blockHistoryNavigation` aborts the pop,
 * vue-router steps forward onto the spare entry again, and the press becomes an in-app back.
 *
 * iOS gets no spare entry on purpose — that entry is exactly what the edge-swipe would animate to.
 */
let androidBackSentinelInstalled = false

function installAndroidBackSentinel() {
  if (androidBackSentinelInstalled || !nativeAppMode.value || isAppleTouchDevice())
    return

  androidBackSentinelInstalled = true
  browserPush(routerHistory.location)
}

/**
 * The scroll rules for a navigation. Applied to the window by vue-router, or to the phone shell's
 * scroller by `scrollBehavior` below.
 */
function resolveNavigationScroll(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  if (to.hash)
    return { el: to.hash, behavior: 'smooth', top: 60 } as const

  // Switching between open workspace tabs must NOT jump to the top — the host
  // restores each tab's own scroll offset. Returning false leaves the scroll
  // position untouched so the two do not fight.
  if (isWorkspaceTabSwitch(to, from))
    return false

  // A QUERY-ONLY change is same-page state - a tab, a filter, a page number - not a navigation,
  // so the reader must keep their place. `useListStatePersistence` writes filters through
  // `router.replace`, which meant every keystroke in a search box scrolled the list to the top.
  //
  // ⚠️ Paging is the one case that DOES want the top, and it no longer gets it for free here:
  // `EntityListPagination` scrolls explicitly after a page change. If you remove this rule, remove
  // that scroll too, or the two will fight.
  if (from.path && to.path === from.path)
    return false

  return { top: 0 } as const
}

const router = createRouter({
  history: routerHistory,
  scrollBehavior(to, from) {
    const position = resolveNavigationScroll(to, from)

    // ⚠️ On a phone the window never scrolls — `main.mobile-shell__content` does — and vue-router
    // only knows how to scroll the window. Apply the same rules to the shell's scroller and tell
    // vue-router there is nothing for it to do.
    const shell = getShellScroller()
    if (!shell || !position)
      return position

    if ('el' in position && position.el) {
      try {
        document.querySelector(position.el)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      catch {
        // An id that is not a valid selector; stay where we are.
      }
    }
    else {
      shell.scrollTo({ top: position.top, behavior: 'auto' })
    }

    return false
  },
  extendRoutes: pages => [
    ...[...pages].map(route => recursiveLayouts(route)),
  ],
})

let accessLoadedForSession = false
let accessLoadPromise: Promise<void> | null = null
const ACCESS_LOAD_TIMEOUT_MS = 4000

function hasPersistedAuth(): boolean {
  const accessToken = authStorage.getAccessToken()
  const refreshToken = authStorage.getRefreshToken()

  if (accessToken || refreshToken)
    return true

  if (authStorage.getSessionAuthenticated())
    return true

  const { api_key, api_secret } = loadAuth()

  return Boolean(api_key && api_secret)
}

async function loadAccessForRouter() {
  try {
    const backendSnapshot = await GetCurrentAccess()

    if (backendSnapshot) {
      const roles = savePermissions(backendSnapshot.roles)

      saveAccessSnapshot({
        ...backendSnapshot,
        roles,
      })

      return
    }
  } catch (error: any) {
    const status = error?.response?.status

    // A REFUSAL IS NOT AN ABSENCE.
    //
    // The `get_roles` fallback below answers exactly one question: "is the
    // dynamic-access backend deployed yet?" It is the wrong answer to "the
    // backend refused this request", which means the session died or permissions
    // changed. Falling through on a 401/403 overwrote a perfectly good snapshot
    // with a zero-page DENIED one, after which `canPage()` returned false for
    // everything and the guard told an authorized user they were not
    // authorized. That was the production bug.
    //
    // The session-expiry path in services/httpClient.ts already owns this
    // outcome (Guest probe → logout → the guard's redirect), so rethrow: the
    // caller must see a real failure rather than a manufactured downgrade. The
    // existing snapshot is left untouched, so a user whose permissions are fine
    // keeps working. This also removes a wasted duplicate `get_roles` 403.
    if (status === 401 || status === 403) {
      console.warn('Access load REFUSED by backend (session or permissions) — keeping the existing snapshot, not falling back to roles', error)

      throw error
    }

    console.warn('Router failed to fetch dynamic access snapshot; using roles for admin detection', error)
  }

  // Deliberately NOT caught: the caller must be able to tell a failed access
  // load from a successful one. Swallowing this used to make every outcome look
  // like success, which is what let one transient failure latch the session
  // onto a stale snapshot.
  const response = await axiosInstance.get('/api/method/frappe.core.doctype.user.user.get_roles')
  const roles = savePermissions(response.data)
  const snapshot = isPermissionRoleFallbackEnabled()
    ? buildAccessSnapshotFromRoles(roles)
    : buildDeniedAccessSnapshotFromRoles(roles)

  saveAccessSnapshot(snapshot)
}

/**
 * Re-read who this user is, right now, on demand.
 *
 * The access snapshot is cached for 8 hours and `ensureAccessLoadedForRouter`
 * latches after its first success, so a permission or till GRANTED mid-session is
 * invisible until the user signs out. That is what the Sync button fixes, and it
 * calls this.
 *
 * Deliberately the SAME function the router uses rather than a second copy: it
 * carries the rule that a refusal is not an absence — a 401/403 rethrows and
 * leaves the existing snapshot untouched, because overwriting it with a zero-page
 * snapshot told authorized users they were not authorized.
 *
 * It also clears the session latch on success, so later navigations re-verify
 * against the snapshot this just stored rather than the one they latched onto.
 */
export async function refreshAccessSnapshot() {
  await loadAccessForRouter()

  accessLoadedForSession = true
}

async function ensureAccessLoadedForRouter() {
  if (accessLoadedForSession && getAccessSnapshot())
    return

  // OFFLINE: do not pay for a call that cannot land. The latch below is set only
  // on SUCCESS (deliberately — see its comment), so offline this function used to
  // start a doomed request and then block the navigation on the 4s timeout race,
  // on EVERY navigation, showing an empty themed shell each time. The cached
  // snapshot is what we would render with anyway once the timeout fired, so read
  // it now and move on. Nothing is latched, so the first navigation after the
  // connection returns verifies access again.
  if (isOffline()) {
    if (getAccessSnapshot())
      return

    // No snapshot at all. There is still nothing a network call can do offline,
    // and waiting 4s does not change the outcome — the guard's own permission
    // check decides what this user may see.
    console.warn('Router access load skipped: the server is unreachable and no cached access snapshot exists')

    return
  }

  if (!accessLoadPromise) {
    accessLoadPromise = loadAccessForRouter()
      .then(() => {
        // Latch ONLY on success. This used to live in `.finally`, so a single
        // transient failure marked access as loaded for the rest of the session
        // and pinned every later navigation to the stale snapshot with no
        // retry. Leaving the flag false means the next navigation tries again.
        accessLoadedForSession = true
      })
      .catch(error => {
        console.warn('Router access load failed; access is UNVERIFIED and will be retried on the next navigation', error)
      })
      .finally(() => {
        accessLoadPromise = null
      })
  }

  // The timeout only keeps a slow network from white-screening the app — it
  // carries no authority. It does not latch, it does not mark access verified,
  // and the in-flight load keeps running and still saves its snapshot when it
  // lands. Whether the user may actually see the data is decided by the
  // backend's answer to the real request (see the 403/Guest handling in
  // services/httpClient.ts), not by whatever cached access we render with here.
  await Promise.race([
    accessLoadPromise,
    new Promise<void>(resolve => {
      window.setTimeout(() => {
        console.warn('Router access load timed out; rendering with UNVERIFIED cached access — the backend remains authoritative')
        resolve()
      }, ACCESS_LOAD_TIMEOUT_MS)
    }),
  ])
}

function isNotAuthorizedTarget(target: any) {
  if (!target)
    return true

  if (typeof target === 'string')
    return target === '/not-authorized'

  return target.name === 'not-authorized' || target.path === '/not-authorized'
}

/**
 * Naming the page key turns "you are not authorized" into something an administrator can
 * act on: these keys are granted one by one under Permissions → Page access, and a whole
 * module can be missing simply because its keys were never synced. Without the key the
 * banner said only that something was refused, for every module in the app.
 */
function showAuthorizationWarning(permissionKey?: string | null) {
  const store = useAuthorizationWarningStore(getActivePinia())

  if (!permissionKey) {
    store.show()

    return
  }

  const i18n = getI18n().global
  const message = i18n.t('alkokh.errors.notAuthorizedWarningPage', { key: permissionKey })

  store.show(message === 'alkokh.errors.notAuthorizedWarningPage' ? undefined : message)
}

function getWarehouseSupplierRedirect(to: { path: string; query: any; hash: string }) {
  if (to.path !== '/suppliers' && !to.path.startsWith('/suppliers/'))
    return null

  return {
    path: to.path.replace(/^\/suppliers(?=\/|$)/, '/warehouse/suppliers'),
    query: to.query,
    hash: to.hash,
  }
}

// Registered FIRST so a blocked history pop never reaches the permission gate or Vuetify's
// overlay close-on-back guard.
router.beforeEach(function blockHistoryNavigation() {
  if (!pendingHistoryPop)
    return true

  pendingHistoryPop = false

  if (!nativeAppMode.value)
    return true

  handleSystemBack()

  return false
})

/**
 * A system back that reached us (Android's button or gesture — iOS has no entry to swipe to).
 *
 * ⚠️ IT NEVER CLOSES A DIALOG. It used to dispatch Escape at the top overlay, and on iOS the
 * edge-swipe arrived the same way — so a sideways swipe threw away a half-filled create/edit form.
 * Dialogs now close only through their own Cancel/✕. With an overlay open the press does nothing;
 * otherwise it is an in-app back, deferred until vue-router has finished restoring history.
 */
function handleSystemBack() {
  if (typeof document === 'undefined' || document.querySelector('.v-overlay--active'))
    return

  setTimeout(() => {
    void navigateBack()
  })
}

router.beforeEach(async (to, from) => {
  // Tells the axios interceptor that a guard is resolving, so a 403 it sees must
  // be handed back here as a flag rather than pushed as its own navigation.
  setNavigationInFlight(true)

  // The tail-wagging cat + the phone tab bar's instant highlight. Not for the first load (the boot
  // splash covers it) and not for a query-only change, which is same-page state, not a page change.
  if (from !== START_LOCATION && to.path !== from.path)
    startRouteLoading(to)

  const supplierRedirect = getWarehouseSupplierRedirect(to)
  if (supplierRedirect)
    return supplierRedirect

  const isPublicRoute = Boolean(to.meta.public)
  const isAuthenticated = hasPersistedAuth()

  if (to.name === 'not-authorized' && !isAuthenticated) {
    return {
      path: '/login',
      query: {
        redirect: '/',
      },
    }
  }

  if (!isPublicRoute && !isAuthenticated) {
    return {
      path: '/login',
      query: {
        redirect: to.fullPath,
      },
    }
  }

  if (isPublicRoute && isAuthenticated && to.path === '/login') {
    await ensureAccessLoadedForRouter()

    const redirectTarget = typeof to.query.redirect === 'string' && to.query.redirect
      ? to.query.redirect
      : getDefaultAuthenticatedRoute()

    return redirectTarget
  }

  if (!isPublicRoute && isAuthenticated) {
    await ensureAccessLoadedForRouter()

    // The access load just talked to the backend. If it came back Guest, the
    // interceptor has already logged us out and toasted — it left the redirect
    // to us, because we are the navigation that is resolving. Returning a
    // redirect here REPLACES this navigation instead of racing it, so the
    // out-in transition never unmounts the old view without mounting a new one.
    if (consumeSessionExpired()) {
      return {
        path: '/login',
        query: { redirect: to.fullPath },
      }
    }
  }

  // Lock reception-only staff to the chrome-free kiosk.
  if (
    !isPublicRoute
    && isAuthenticated
    && to.path !== '/clinic-station'
    && to.name !== 'not-authorized'
    && isClinicReceptionOnlyUser()
  )
    return { path: '/clinic-station' }

  // Gate the kiosk by the Clinic Station page role (works in backend +
  // roles-fallback modes) and skip the generic page-permission check that follows.
  if (isAuthenticated && to.path === '/clinic-station')
    return canPage('page.clinic.station') ? undefined : { name: 'not-authorized' }

  if (!isPublicRoute && isAuthenticated && to.path !== '/') {
    const meta = to.meta as Record<string, unknown>
    const metaPermissionKey = typeof meta.permissionKey === 'string'
      ? meta.permissionKey
      : null
    const permissionKey = metaPermissionKey ?? getPageKeyForRoute(to.path, to.name)

    if (
      permissionKey
      && !canPage(permissionKey)
      && to.name !== 'not-authorized'
    ) {
      showAuthorizationWarning(permissionKey)

      if (from.name)
        return false

      const defaultRoute = getDefaultAuthenticatedRoute()

      return isNotAuthorizedTarget(defaultRoute)
        ? { name: 'not-authorized' }
        : defaultRoute
    }
  }

  if (isAuthenticated && to.path === '/') {
    const defaultRoute = getDefaultAuthenticatedRoute()

    if (typeof defaultRoute === 'string')
      return defaultRoute === '/' ? undefined : defaultRoute

    if (defaultRoute?.path === '/' || defaultRoute?.name === 'root')
      return undefined

    return defaultRoute
  }
})

// Clear the in-flight marker however the navigation ends. `afterEach` in Vue
// Router 4 fires for FAILED navigations too (aborts, redirects, cancellations —
// the failure is the third argument), so this cannot leave the flag stuck true
// and permanently suppress the interceptor's own redirect. `onError` covers a
// guard throwing.
router.afterEach((to, from, failure) => {
  setNavigationInFlight(false)
  endRouteLoading(to)

  // A pop aborted by a leave guard never reaches `blockHistoryNavigation`, and a tab tap whose
  // navigation never landed never consumed its root flag; drop both so they cannot leak into the
  // next navigation.
  pendingHistoryPop = false
  nextPushIsRoot = false

  // Register the open record as a workspace tab. Guarded on `failure` because
  // `afterEach` also fires for aborted navigations — the permission guard returns
  // false in place (see beforeEach), and a denied record must not open a tab.
  if (failure)
    return

  // Login / public pages end the session's trail: a back arrow must never lead from the app into
  // the sign-in screen, or from a new session into the previous user's pages.
  if (to.meta.public || from.meta.public)
    clearInAppHistory()

  const tabs = useWorkspaceTabsStore(getActivePinia())
  if (tabs.isTabbable(to))
    tabs.registerVisit(to, resolveWorkspaceTabTitle(to))
})

// STALE-BUILD RECOVERY. Every page is a lazy chunk with a content-hashed name.
// After a deploy the previous build's chunks are gone, so a tab opened before the
// deploy 404s on the first route it has not visited yet: `import()` rejects, the
// navigation dies, and the user is stuck on the old page until they refresh by
// hand. Do that refresh for them — straight onto the page they clicked.
//
// ⚠️ THREE DELIBERATE LIMITS, because a reload destroys in-memory work:
//
//  1. NAVIGATION BOUNDARY ONLY — `router.onError`, never a blanket
//     `vite:preloadError` listener. `import()` is used for far more than routes
//     here (pet-profile tabs, WhatsApp/settings tabs, invoice + certificate PDFs,
//     HEIC decode, lab OCR). A listener on the raw Vite event would reload a
//     doctor mid-visit the moment they opened a PDF. On a route error the user
//     has already asked to leave the page, so nothing is mid-edit.
//  2. ONCE PER TAB — a COUNT, not a cooldown. A time window is a rate limit, not
//     a cap: a permanently missing chunk would reload every 10s forever. After
//     one attempt the navigation just fails as it did before this handler — no
//     worse than the old behaviour, and no reload loop.
//  3. NEVER OVER OPEN POS WORK — `posSales` carts are memory-only on purpose
//     (see the header of stores/posSales.store.ts), so a reload loses every open
//     sale. A cashier is left alone; their next manual refresh recovers the tab.
//
// Other dirty screens (stock transfer, lab results, mobile-home builder) keep
// their own `beforeunload` guards, which still fire for a programmatic reload.
const CHUNK_RELOAD_KEY = 'alkokh:stale-build-reloaded'

function isChunkLoadError(error: unknown) {
  const message = String((error as { message?: unknown })?.message ?? error ?? '')

  // ⚠️ The MIME patterns are NOT padding. This SPA is served with a catch-all
  // rewrite (`pm2 serve --spa` / nginx `try_files … /index.html`), so a chunk
  // that no longer exists does not 404 — it returns index.html with a 200. The
  // browser then rejects the import for its TYPE, not for a failed fetch, and
  // the message says "MIME type" rather than "Failed to fetch". Without these,
  // the handler would miss the single most common shape of this bug here.
  // `public/alkokh-sw.js:37` documents the same rewrite from the cache side.
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading (CSS )?chunk .* failed|Expected a JavaScript(-or-Wasm)? module script|disallowed MIME type|not a valid JavaScript MIME type/i.test(message)
}

function hasOpenPosWork() {
  try {
    // `pos-sales` is the store id in stores/posSales.store.ts. Absent = POS was
    // never opened in this tab, so there is no cart to lose.
    const slots = (getActivePinia()?.state.value as Record<string, any> | undefined)?.['pos-sales']?.slots

    if (!Array.isArray(slots))
      return false

    return slots.some(slot => posSaleSlotHasWork(slot))
  }
  catch {
    // Unexpected shape — assume work is open. A missed reload costs one manual
    // refresh; a wrong one costs a cashier their open sales.
    return true
  }
}

function reloadOnceForStaleBuild(href: string) {
  // OFFLINE: a chunk that will not load offline is not a stale build, it is a
  // chunk that was never cached. Reloading cannot fetch it and on plain HTTP
  // (no service worker, so no cached shell) the reload lands on a blank page and
  // takes the whole session — including anything not yet synced — with it.
  if (isOffline()) {
    console.warn('Chunk failed to load while the server is unreachable — NOT reloading. The page will work again once the connection returns.')

    return
  }

  if (hasOpenPosWork()) {
    console.warn('Stale build detected, but POS has open sales — NOT reloading. The cashier must refresh when their till is clear.')

    return
  }

  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY))
      return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  }
  catch {
    // Storage blocked — reload anyway; without the marker this can repeat at
    // most once per navigation attempt, and only when a chunk is really missing.
  }

  // Electron runs on hash history, where assigning a `#/path` href changes the
  // hash WITHOUT reloading — the stale chunk would never be re-fetched.
  if (isElectronRuntime()) {
    window.location.hash = href.replace(/^[^#]*#/, '')
    window.location.reload()
  }
  else {
    window.location.assign(href)
  }
}

router.onError((error, to) => {
  setNavigationInFlight(false)
  endRouteLoading(to)

  // A first navigation that died never mounts a layout; do not leave the boot cat over it.
  hideBootSplash()

  if (to && isChunkLoadError(error))
    reloadOnceForStaleBuild(router.resolve(to).href)
})

// The spare entry is pushed once the first navigation has settled (it must copy the real URL), and
// again if native-app mode only turns on later — a desktop window narrowed past the breakpoint.
void router.isReady().then(() => {
  watch(nativeAppMode, installAndroidBackSentinel, { immediate: true })
})

export { router }

export default function (app: App) {
  app.use(router)
}
